(function () {
  if (window.__fpProductAnchorNavBootstrapped) return;
  window.__fpProductAnchorNavBootstrapped = true;

  const SECTION_SELECTOR = '[data-fp-anchor-nav-section]';
  const HEADER_SELECTOR = '.section-header .header-wrapper';
  const MAIN_PRODUCT_INFO_SELECTORS = [
    '[id^="shopify-section-template--"][id$="__main"]',
    '[id^="shopify-section-main"]',
    '.product-information[data-testid="product-information"]',
    '[data-testid="product-information"]',
    '#shopify-section-main',
    '[data-section-id="main"]',
    '#MainProduct',
    '.main-product',
    '.fp-product-information',
  ];
  const MOBILE_QUERY = '(max-width: 989px)';
  const instances = new Map();

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  class FPProductAnchorNav {
    constructor(sectionRoot) {
      this.sectionRoot = sectionRoot;
      this.stickyAnchor = sectionRoot.closest('.shopify-section') || sectionRoot;
      this.nav = sectionRoot.querySelector('.fp-product-anchor-nav');
      this.scroller = sectionRoot.querySelector('.fp-product-anchor-nav__scroller');
      this.links = this.scroller ? Array.from(this.scroller.querySelectorAll('.fp-product-anchor-nav__link')) : [];

      if (!this.nav || !this.scroller || !this.links.length) {
        this.nav = null;
        return;
      }

      this.stickyEnabled = this.nav.dataset.stickyEnabled === 'true';
      this.showShadow = this.nav.dataset.enableShadow === 'true';
      this.extraOffset = Number(this.nav.dataset.extraOffset || 0);
      this.smoothScrollEnabled = this.nav.dataset.smoothScroll === 'true';

      this.mobileTriggerMode = this.nav.dataset.mobileTriggerMode || 'after_progress';
      this.mobileTriggerSource = this.nav.dataset.mobileTriggerSource || 'main_product_information';
      this.mobileTriggerCustomSelector = (this.nav.dataset.mobileTriggerCustomSelector || '').trim();
      this.mobileTriggerStartPercent = clamp(Number(this.nav.dataset.mobileTriggerStartPercent || 90), 0, 100);
      this.mobileOnlyBehavior = this.nav.dataset.mobileOnlyBehavior || 'mobile_only';
      this.mobileKeepVisibleOnceTriggered = this.nav.dataset.mobileKeepVisibleOnceTriggered === 'true';
      this.mobileHiddenState = this.nav.dataset.mobileHiddenState || 'translate_y';

      this.targets = [];
      this.scrollFrame = null;
      this.triggerSourceElement = null;
      this.triggerY = 0;
      this.mobileHasTriggered = false;
      this.isFixed = false;
      this.lockedActiveLink = null;
      this.lockedTarget = null;
      this.lockReleaseAt = 0;

      this.onClick = this.onClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onAnimationFrame = this.onAnimationFrame.bind(this);

      this.scroller.addEventListener('click', this.onClick);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      if ('ResizeObserver' in window) {
        this.headerResizeObserver = new ResizeObserver(() => this.refresh());
        const header = document.querySelector(HEADER_SELECTOR);
        if (header) this.headerResizeObserver.observe(header);

        this.sectionResizeObserver = new ResizeObserver(() => this.updateFixedPosition());
        this.sectionResizeObserver.observe(this.sectionRoot);
      }

      this.refresh();
    }

    isMobileViewport() {
      return window.matchMedia(MOBILE_QUERY).matches;
    }

    shouldControlMobileVisibility() {
      if (this.mobileTriggerMode !== 'after_progress') return false;
      if (this.mobileOnlyBehavior === 'all_breakpoints') return true;
      return this.isMobileViewport();
    }

    resolveTriggerSourceElement() {
      if (this.mobileTriggerSource === 'custom_selector' && this.mobileTriggerCustomSelector) {
        return document.querySelector(this.mobileTriggerCustomSelector);
      }

      const navTop = this.sectionRoot.getBoundingClientRect().top + window.scrollY;
      const candidates = [];
      const seen = new Set();

      for (const selector of MAIN_PRODUCT_INFO_SELECTORS) {
        document.querySelectorAll(selector).forEach((el) => {
          if (seen.has(el)) return;
          seen.add(el);
          candidates.push(el);
        });
      }

      if (!candidates.length) return null;

      const positioned = candidates
        .map((el) => ({
          el,
          top: el.getBoundingClientRect().top + window.scrollY,
        }))
        .sort((a, b) => a.top - b.top);

      const nearestBeforeNav = positioned.filter((item) => item.top <= navTop + 1).pop();
      if (nearestBeforeNav) return nearestBeforeNav.el;

      return positioned[0].el;
    }

    refreshTriggerMetrics() {
      this.triggerSourceElement = this.resolveTriggerSourceElement();
      if (!this.triggerSourceElement) {
        this.triggerY = 0;
        return;
      }

      const rect = this.triggerSourceElement.getBoundingClientRect();
      const sourceTop = rect.top + window.scrollY;
      const sourceHeight = Math.max(rect.height, 0);
      this.triggerY = sourceTop + sourceHeight * (this.mobileTriggerStartPercent / 100);
    }

    getHeaderBottom() {
      const header = document.querySelector(HEADER_SELECTOR);
      if (!header) return 0;
      const rect = header.getBoundingClientRect();
      return rect.top < window.innerHeight ? Math.max(0, Math.round(rect.bottom)) : 0;
    }

    getStickyTop() {
      return this.getHeaderBottom() + this.extraOffset;
    }

    getScrollOffset() {
      return this.getStickyTop() + (this.stickyEnabled ? this.nav.offsetHeight : 0);
    }

    updateStickyTop() {
      if (!this.stickyEnabled) return;
      this.nav.style.setProperty('--fp-nav-sticky-top', `${this.getStickyTop()}px`);
    }

    refreshTargets() {
      this.targets = this.links
        .map((link) => {
          const targetId = (link.dataset.targetId || '').replace(/^#/, '').trim();
          const target = targetId ? document.getElementById(targetId) : null;

          if (!targetId || !target) {
            link.classList.add('is-disabled');
            link.setAttribute('aria-disabled', 'true');
            return null;
          }

          link.classList.remove('is-disabled');
          link.removeAttribute('aria-disabled');
          return { link, target };
        })
        .filter(Boolean);

      if (this.lockedTarget && !this.targets.some((entry) => entry.target === this.lockedTarget)) {
        this.clearActiveLock();
      }
    }

    setActive(activeLink) {
      this.links.forEach((link) => {
        const isActive = link === activeLink;
        link.classList.toggle('is-active', isActive);
        if (isActive) {
          link.setAttribute('aria-current', 'page');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    syncActiveLink() {
      if (!this.targets.length) return;

      if (this.lockedActiveLink && this.lockedTarget) {
        const checkpoint = window.scrollY + this.getScrollOffset();
        const reachedLockedTarget = Math.abs(checkpoint - this.lockedTarget.offsetTop) <= 16;
        const lockExpired = window.performance.now() >= this.lockReleaseAt;

        if (!reachedLockedTarget && !lockExpired) {
          this.setActive(this.lockedActiveLink);
          return;
        }

        this.clearActiveLock();
      }

      const checkpoint = window.scrollY + this.getScrollOffset();
      let activeLink = this.targets[0].link;

      this.targets.forEach((entry) => {
        if (checkpoint >= entry.target.offsetTop) {
          activeLink = entry.link;
        }
      });

      this.setActive(activeLink);
    }

    lockActiveLink(link, target) {
      this.lockedActiveLink = link;
      this.lockedTarget = target;
      this.lockReleaseAt = window.performance.now() + (this.smoothScrollEnabled ? 2000 : 250);
    }

    clearActiveLock() {
      this.lockedActiveLink = null;
      this.lockedTarget = null;
      this.lockReleaseAt = 0;
    }

    syncMobileVisibility() {
      const shouldControl = this.shouldControlMobileVisibility();
      this.nav.dataset.mobileOnlyBehavior = this.mobileOnlyBehavior;
      this.nav.dataset.mobileHiddenState = this.mobileHiddenState;
      this.nav.dataset.mobileVisibilityControlled = shouldControl ? 'true' : 'false';

      if (!shouldControl) {
        this.nav.dataset.mobileVisible = 'true';
        return;
      }

      if (!this.triggerSourceElement) {
        this.nav.dataset.mobileVisible = 'true';
        return;
      }

      const thresholdReached = window.scrollY + this.getStickyTop() >= this.triggerY;
      if (thresholdReached) {
        this.mobileHasTriggered = true;
      }

      const isVisible = thresholdReached || (this.mobileKeepVisibleOnceTriggered && this.mobileHasTriggered);
      this.nav.dataset.mobileVisible = isVisible ? 'true' : 'false';
    }

    setFixedState(shouldFix) {
      if (this.isFixed === shouldFix) {
        if (shouldFix) {
          this.sectionRoot.style.setProperty('--fp-nav-placeholder-height', `${this.nav.offsetHeight}px`);
          this.updateFixedPosition();
        }
        return;
      }

      this.isFixed = shouldFix;
      this.sectionRoot.classList.toggle('fp-anchor-nav--fixed-active', shouldFix);
      this.nav.classList.toggle('is-stuck-fixed', shouldFix);

      if (shouldFix) {
        this.sectionRoot.style.setProperty('--fp-nav-placeholder-height', `${this.nav.offsetHeight}px`);
        this.updateFixedPosition();
      } else {
        this.sectionRoot.style.removeProperty('--fp-nav-placeholder-height');
        this.nav.style.removeProperty('--fp-nav-fixed-left');
        this.nav.style.removeProperty('--fp-nav-fixed-width');
      }
    }

    updateFixedPosition() {
      if (!this.isFixed) return;

      const rect = this.getFixedBounds();
      const width = Math.max(0, Math.round(rect.width));
      const left = Math.round(rect.left);
      this.nav.style.setProperty('--fp-nav-fixed-left', `${left}px`);
      this.nav.style.setProperty('--fp-nav-fixed-width', `${width}px`);
    }

    getFixedBounds() {
      if (!window.matchMedia('(min-width: 990px)').matches) {
        return this.sectionRoot.getBoundingClientRect();
      }

      const headerWrapper = document.querySelector('.section-header .header-wrapper, .header-wrapper');
      if (!headerWrapper) {
        return this.nav.getBoundingClientRect();
      }

      const rect = headerWrapper.getBoundingClientRect();
      if (rect.width <= 0) {
        return this.nav.getBoundingClientRect();
      }

      return rect;
    }

    syncStickyShadow() {
      const navIsHidden = this.nav.dataset.mobileVisibilityControlled === 'true' && this.nav.dataset.mobileVisible === 'false';

      if (navIsHidden || !this.stickyEnabled) {
        this.setFixedState(false);
        this.nav.classList.remove('is-stuck');
        return;
      }

      // The section remains in normal flow as the nav becomes fixed. Its live
      // position avoids an early stick after fonts or product media resize.
      const hasReachedStickyPoint = this.stickyAnchor.getBoundingClientRect().top <= this.getStickyTop() + 1;
      this.setFixedState(hasReachedStickyPoint);
      this.nav.classList.toggle('is-stuck', hasReachedStickyPoint && this.showShadow);
    }

    refresh() {
      this.refreshTargets();
      this.refreshTriggerMetrics();
      this.updateStickyTop();

      this.syncMobileVisibility();
      this.syncActiveLink();
      this.syncStickyShadow();
    }

    onClick(event) {
      const link = event.target.closest('.fp-product-anchor-nav__link');
      if (!link || !this.scroller.contains(link) || link.classList.contains('is-disabled')) return;

      const targetId = (link.dataset.targetId || '').replace(/^#/, '').trim();
      const target = targetId ? document.getElementById(targetId) : null;
      if (!target) return;

      event.preventDefault();
      const top = target.getBoundingClientRect().top + window.scrollY - this.getScrollOffset();
      window.scrollTo({
        top: Math.max(0, top),
        behavior: this.smoothScrollEnabled ? 'smooth' : 'auto',
      });
      this.lockActiveLink(link, target);
      this.setActive(link);
    }

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(this.onAnimationFrame);
    }

    onAnimationFrame() {
      this.updateStickyTop();
      this.syncMobileVisibility();
      this.syncActiveLink();
      this.syncStickyShadow();
      this.scrollFrame = null;
    }

    onResize() {
      this.refresh();
    }

    destroy() {
      if (this.scrollFrame) {
        window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }

      this.scroller.removeEventListener('click', this.onClick);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);

      if (this.headerResizeObserver) {
        this.headerResizeObserver.disconnect();
      }
      if (this.sectionResizeObserver) {
        this.sectionResizeObserver.disconnect();
      }

      this.setFixedState(false);
      this.nav.classList.remove('is-stuck');
      this.nav.dataset.mobileVisible = 'true';
      this.nav.dataset.mobileVisibilityControlled = 'false';
      this.clearActiveLock();
      this.links = [];
      this.targets = [];
      this.triggerSourceElement = null;
    }
  }

  function initSection(sectionRoot) {
    if (!sectionRoot || instances.has(sectionRoot)) return;

    const instance = new FPProductAnchorNav(sectionRoot);
    if (!instance.nav) return;
    instances.set(sectionRoot, instance);
  }

  function destroySection(sectionRoot) {
    const instance = instances.get(sectionRoot);
    if (!instance) return;

    instance.destroy();
    instances.delete(sectionRoot);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  function refreshSection(sectionRoot) {
    const instance = instances.get(sectionRoot);
    if (!instance) return;
    instance.refresh();
  }

  // Connected color changes replace MainContent without executing its scripts.
  // Release the old page listeners before binding the navigation in the new PDP.
  document.addEventListener('fp:connected-product-swapped', () => {
    instances.forEach((instance, sectionRoot) => {
      if (!sectionRoot.isConnected) destroySection(sectionRoot);
    });
    initAll(document.getElementById('MainContent'));
  });

  document.addEventListener('fp:connected-product-settled', () => {
    instances.forEach((instance) => instance.refresh());
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target;
    if (!root) return;

    if (root.matches?.('.section-header')) {
      instances.forEach((instance) => {
        instance.headerResizeObserver?.disconnect();
        const header = document.querySelector(HEADER_SELECTOR);
        if (header) instance.headerResizeObserver?.observe(header);
        instance.refresh();
      });
    }

    if (root.matches && root.matches(SECTION_SELECTOR)) {
      initSection(root);
      return;
    }

    if (root.querySelectorAll) {
      initAll(root);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target;
    if (!root) return;

    if (instances.has(root)) {
      destroySection(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll(SECTION_SELECTOR).forEach(destroySection);
    }
  });

  document.addEventListener('shopify:section:select', (event) => {
    const root = event.target;
    if (!root) return;

    if (instances.has(root)) {
      refreshSection(root);
    }
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const root = event.target;
    if (!root) return;

    if (instances.has(root)) {
      refreshSection(root);
    }
  });
})();
